import { log } from "utils/log";

import { fetchTileSetFile } from "./tiledSource";
import { type TiledDrawingSource, tileRelativePath } from "./tileMath";

const l = log.miniMap.tiled;

/** Concurrent tile download cap — a fast pan must not spike memory on mobile. */
const MAX_CONCURRENT_TILE_FETCHES = 8;

let activeFetches = 0;
/**
 * Waiting requests, most-recently-queued last. Released LIFO (as a stack, via
 * `pop`), not FIFO: every draw pass only requests tiles inside the *current*
 * viewport (`drawTiledImage.ts`), so the most recently queued request is
 * whatever the user is looking at right now. A FIFO queue would instead serve
 * requests in original request order — after a zoom/pan, that means draining
 * a backlog from the *previous* viewport (or the initial full-image fit view)
 * before ever reaching the new one, which is the delay this fixes.
 */
const fetchQueue: (() => void)[] = [];

function acquireFetchSlot(): Promise<void> {
    if (activeFetches < MAX_CONCURRENT_TILE_FETCHES) {
        activeFetches++;
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        fetchQueue.push(() => {
            activeFetches++;
            resolve();
        });
    });
}

function releaseFetchSlot(): void {
    activeFetches--;
    const next = fetchQueue.pop();
    if (next) {
        next();
    }
}

/**
 * LRU of decoded tile bitmaps for one drawing. Resident memory is bounded by
 * `maxTiles * ~256 KB` regardless of document size; evicted bitmaps are
 * closed immediately (the compressed originals stay in the HTTP cache).
 * `getOrRequest` is synchronous: a draw pass paints what's cached and
 * schedules the rest; `onTileLoaded` fires per arrival so the owner repaints.
 *
 * `beginFrame` + the per-key generation in {@link getOrRequest} let a load
 * that finishes after the viewport has moved on drop itself instead of
 * entering the LRU: the LIFO fetch queue alone only reorders *which pending
 * fetch starts next*, it doesn't stop an off-screen tile that already
 * started from completing and evicting a just-loaded current-viewport tile
 * (see BM-13597 code review). Callers that never call `beginFrame` (e.g.
 * tests driving `getOrRequest` directly) get the old behavior: every tile
 * is always considered wanted.
 */
export class TileBitmapCache {
    private tiles = new Map<string, ImageBitmap>(); // Insertion order = LRU order (oldest first).
    private pending = new Set<string>();
    private failed = new Set<string>(); // Don't hot-loop retries on broken tiles.
    private disposed = false;
    private currentGeneration = 0;
    private lastWantedGeneration = new Map<string, number>();

    constructor(
        private source: TiledDrawingSource,
        private options: { maxTiles: number; onTileLoaded: () => void },
    ) {}

    /** Mark the start of a new paint pass: tiles not re-requested before the next call become stale. */
    beginFrame(): void {
        this.currentGeneration++;
    }

    /** Resident bitmap for the tile (refreshes recency), else schedules a download and returns undefined. */
    getOrRequest(level: number, x: number, y: number): ImageBitmap | undefined {
        const key = `${level}/${x}/${y}`;
        this.lastWantedGeneration.set(key, this.currentGeneration);
        const hit = this.tiles.get(key);
        if (hit) {
            this.tiles.delete(key);
            this.tiles.set(key, hit);
            return hit;
        }
        if (!this.pending.has(key) && !this.failed.has(key)) {
            this.pending.add(key);
            void this.load(key, level, x, y);
        }
        return undefined;
    }

    /** Close every resident bitmap and reject late downloads. */
    dispose(): void {
        this.disposed = true;
        for (const bitmap of this.tiles.values()) {
            bitmap.close();
        }
        this.tiles.clear();
        this.pending.clear();
        this.failed.clear();
    }

    /** Whether `key` was requested in the most recent {@link beginFrame} pass (always true if `beginFrame` is unused). */
    private isStillWanted(key: string): boolean {
        return this.lastWantedGeneration.get(key) === this.currentGeneration;
    }

    private async load(key: string, level: number, x: number, y: number): Promise<void> {
        await acquireFetchSlot();
        try {
            if (this.disposed || !this.isStillWanted(key)) {
                // Viewport moved on before this tile's turn came up -- drop it
                // rather than spending a network fetch on stale work.
                return;
            }
            const relativePath = tileRelativePath(this.source, level, x, y);
            const response = await fetchTileSetFile(this.source, relativePath);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status} for tile ${relativePath}`);
            }
            const blob = await response.blob();
            const bitmap = await createImageBitmap(blob);
            if (this.disposed || !this.isStillWanted(key)) {
                // Viewport moved on while this fetch/decode was in flight --
                // discard instead of inserting, so it can't evict a
                // current-viewport tile from the LRU (BM-13597 code review).
                bitmap.close();
                return;
            }
            this.tiles.set(key, bitmap);
            while (this.tiles.size > this.options.maxTiles) {
                const oldestKey = this.tiles.keys().next().value as string;
                const oldest = this.tiles.get(oldestKey);
                this.tiles.delete(oldestKey);
                oldest?.close();
            }
            this.options.onTileLoaded();
        } catch (error) {
            this.failed.add(key);
            l.warn("Failed to load drawing tile:", key, error);
        } finally {
            this.pending.delete(key);
            releaseFetchSlot();
        }
    }
}
